import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { mdiAlertOutline, mdiInformationOutline } from '@mdi/js'

import { apiClient } from '../../../../api/client'
import type { SessionPolicy, SessionPolicyUpdate } from '../../../../api/schema'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { useCan } from '../../../../contexts/PermissionsContext'
import { notify } from '../../../../lib/notifications'
import { AppIcon } from '../../../../components/ui/icon'
import { Checkbox } from '../../../../components/ui/checkbox'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'

export const Route = createFileRoute('/$org/settings/security/')({
    component: SecuritySettingsPage,
})

const SAVED_KEY = 'security_settings_saved'
const ERROR_KEY = 'security_settings_error'

/** Select value standing for "not set" (a Radix Select item can't be ''). */
const UNSET = 'unset'

const DURATION_PRESETS = ['1h', '4h', '8h', '12h', '24h', '3d', '7d']
const IDLE_PRESETS = ['2h', '4h', '8h', '24h', '3d']
const SESSION_PRESETS = [1, 2, 3, 5]

/** formatDuration renders a policy duration ("8h", "7d", "90m") in words. */
function formatDuration(value: string | null | undefined, t: TFunction): string {
    if (!value) return '—'
    const match = /^(\d+)([mhd])$/.exec(value)
    if (!match) return value
    const count = Number(match[1])
    const unit = { m: 'minutes', h: 'hours', d: 'days' }[match[2] as 'm' | 'h' | 'd']
    return t(`security.units.${unit}`, { count })
}

/** withCurrent adds a stored value the presets don't offer, so it still shows. */
function withCurrent<T>(presets: T[], current: T | null | undefined): T[] {
    if (current === null || current === undefined || presets.includes(current)) return presets
    return [...presets, current]
}

/**
 * The organization's session policy (spec 2026-10-01-01): how long a session
 * lasts, when an idle one ends, and how many devices one person may keep
 * signed in. A platform-wide limit can only make it stricter; the effective
 * policy is shown under the settings so an admin sees what actually applies.
 */
function SecuritySettingsPage() {
    const { org } = Route.useParams()
    const { t } = useTranslation('settings')
    const queryClient = useQueryClient()
    const canWrite = useCan('organization.write')

    const breadcrumbs = useMemo(
        () => [
            { title: t('common:nav.home'), path: `/${org}` },
            { title: t('page.title'), path: `/${org}/settings` },
            { title: t('security.breadcrumb') },
        ],
        [org, t]
    )
    useBreadcrumbs(breadcrumbs)

    const queryKey = ['session-policy', org]
    const { data: policy, isLoading } = useQuery({
        queryKey,
        queryFn: async (): Promise<SessionPolicy> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/session-policy', {
                params: { path: { orgCode: org } },
            })
            if (error || !data) throw new Error('Failed to load the session policy')
            return data.data
        },
    })

    const mutation = useMutation({
        mutationFn: async (body: SessionPolicyUpdate): Promise<SessionPolicy> => {
            const { data, error } = await apiClient.PATCH('/api/v1/organizations/{orgCode}/session-policy', {
                params: { path: { orgCode: org } },
                body,
            })
            if (error || !data) throw new Error('Failed to save the session policy')
            return data.data
        },
        onSuccess: (data) => {
            queryClient.setQueryData(queryKey, data)
            notify({ key: SAVED_KEY, variant: 'default', titleKey: 'settings:security.saved' })
        },
        onError: () => {
            notify({
                key: ERROR_KEY,
                variant: 'destructive',
                titleKey: 'settings:security.error_title',
                descriptionKey: 'settings:security.error_description',
                timeoutMs: 0,
            })
        },
    })

    const save = (body: SessionPolicyUpdate) => mutation.mutate(body)

    if (isLoading || !policy) {
        return (
            <div className="container mx-auto py-8 max-w-3xl text-muted-foreground" data-testid="security-settings-loading">
                {t('security.loading')}
            </div>
        )
    }

    const own = policy.organization
    const platform = policy.platform
    const effective = policy.effective
    // A stored allowed list the server can't honor reads as the strong kinds.
    const factorsDiffer = own.mfa_allowed_factors != null && effective.mfa_allowed_factors != null &&
        [...own.mfa_allowed_factors].sort().join(',') !== [...effective.mfa_allowed_factors].sort().join(',')
    const disabled = !canWrite || mutation.isPending
    const floorStricter =
        platform.enforced &&
        ((platform.session_max_duration != null && effective.session_max_duration === platform.session_max_duration &&
            own.session_max_duration !== platform.session_max_duration) ||
            (platform.max_sessions_per_user != null && effective.max_sessions_per_user === platform.max_sessions_per_user &&
                own.max_sessions_per_user !== platform.max_sessions_per_user))

    return (
        <div className="container mx-auto py-8 max-w-3xl" data-testid="security-settings-page">
            <PageHeader
                className="mb-8"
                icon={SECTION_ICONS.security}
                title={t('security.title')}
                description={t('security.subtitle')}
            />

            <div className="space-y-6">
                <section className="bg-card rounded-lg border border-border p-6 space-y-6" data-testid="security-session-policy">
                    <div>
                        <h2 className="text-base font-semibold text-foreground">{t('security.sessions.title')}</h2>
                        <p className="text-muted-foreground text-sm mt-1">{t('security.sessions.description')}</p>
                    </div>

                    <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                        <div>
                            <div className="text-sm font-medium text-foreground">{t('security.max_duration.label')}</div>
                            <p className="text-xs text-muted-foreground mt-1">{t('security.max_duration.hint')}</p>
                        </div>
                        <Select
                            value={own.session_max_duration ?? '7d'}
                            onValueChange={(value) => save({ session_max_duration: value })}
                            disabled={disabled}
                        >
                            <SelectTrigger data-testid="security-max-duration-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {withCurrent(DURATION_PRESETS, own.session_max_duration).map((value) => (
                                    <SelectItem key={value} value={value} data-testid={`security-max-duration-option-${value}`}>
                                        {formatDuration(value, t)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                        <div>
                            <div className="text-sm font-medium text-foreground">{t('security.idle_timeout.label')}</div>
                            <p className="text-xs text-muted-foreground mt-1">{t('security.idle_timeout.hint')}</p>
                        </div>
                        <Select
                            value={own.session_idle_timeout ?? UNSET}
                            onValueChange={(value) => save({ session_idle_timeout: value === UNSET ? null : value })}
                            disabled={disabled}
                        >
                            <SelectTrigger data-testid="security-idle-timeout-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={UNSET} data-testid="security-idle-timeout-option-unset">
                                    {t('security.idle_timeout.none')}
                                </SelectItem>
                                {withCurrent(IDLE_PRESETS, own.session_idle_timeout).map((value) => (
                                    <SelectItem key={value} value={value} data-testid={`security-idle-timeout-option-${value}`}>
                                        {formatDuration(value, t)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                        <div>
                            <div className="text-sm font-medium text-foreground">{t('security.max_sessions.label')}</div>
                            <p className="text-xs text-muted-foreground mt-1">{t('security.max_sessions.hint')}</p>
                        </div>
                        <Select
                            value={own.max_sessions_per_user != null ? String(own.max_sessions_per_user) : UNSET}
                            onValueChange={(value) => save({ max_sessions_per_user: value === UNSET ? null : Number(value) })}
                            disabled={disabled}
                        >
                            <SelectTrigger data-testid="security-max-sessions-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={UNSET} data-testid="security-max-sessions-option-unset">
                                    {t('security.max_sessions.default', { count: policy.limits.max_sessions_per_user })}
                                </SelectItem>
                                {withCurrent(SESSION_PRESETS, own.max_sessions_per_user).map((value) => (
                                    <SelectItem key={value} value={String(value)} data-testid={`security-max-sessions-option-${value}`}>
                                        {t('security.max_sessions.option', { count: value })}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {!canWrite && (
                        <p className="text-xs text-muted-foreground" data-testid="security-read-only">
                            {t('security.read_only')}
                        </p>
                    )}
                </section>

                <section className="bg-card rounded-lg border border-border p-6 space-y-6" data-testid="security-mfa-policy">
                    <div>
                        <h2 className="text-base font-semibold text-foreground">{t('security.mfa.title')}</h2>
                        <p className="text-muted-foreground text-sm mt-1">{t('security.mfa.description')}</p>
                    </div>

                    <label className="flex items-start gap-3">
                        <Checkbox
                            checked={own.mfa_required}
                            onCheckedChange={(checked) => save({ mfa_required: checked })}
                            disabled={disabled}
                            className="mt-0.5"
                            data-testid="security-mfa-required"
                        />
                        <span>
                            <span className="block text-sm font-medium text-foreground">{t('security.mfa.required_label')}</span>
                            <span className="block text-xs text-muted-foreground mt-1">{t('security.mfa.required_hint')}</span>
                        </span>
                    </label>

                    <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                        <div>
                            <div className="text-sm font-medium text-foreground">{t('security.mfa.factors_label')}</div>
                            <p className="text-xs text-muted-foreground mt-1">{t('security.mfa.factors_hint')}</p>
                        </div>
                        <div className="space-y-2" data-testid="security-mfa-factors">
                            {policy.limits.factor_kinds.map((kind) => {
                                const allowed = own.mfa_allowed_factors ?? policy.limits.factor_kinds
                                const checked = allowed.includes(kind)
                                const available = policy.limits.available_factors.includes(kind)
                                // The list must keep a factor people can enroll today.
                                const lastAvailable = checked && available &&
                                    allowed.filter((k) => policy.limits.available_factors.includes(k)).length === 1
                                return (
                                    <div key={kind}>
                                    <label className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={checked}
                                            disabled={disabled || lastAvailable}
                                            onCheckedChange={(next) => save({
                                                mfa_allowed_factors: (next
                                                    ? policy.limits.factor_kinds.filter((k) => k === kind || allowed.includes(k))
                                                    : allowed.filter((k) => k !== kind)) as NonNullable<SessionPolicyUpdate['mfa_allowed_factors']>,
                                            })}
                                            data-testid={`security-mfa-factor-${kind}`}
                                        />
                                        <span className="text-foreground">{t(`mfa.factor_kinds.${kind}`, { ns: 'auth' })}</span>
                                        {!available && (
                                            <span className="text-xs text-muted-foreground">({t('security.mfa.factors_unavailable')})</span>
                                        )}
                                    </label>
                                    {kind === 'sms' && (
                                        // SIM swapping hands the codes to whoever ports the number.
                                        <p className="mt-1 pl-6 text-xs text-muted-foreground" data-testid="security-mfa-sms-warning">
                                            {t('security.mfa.sms_weak')}
                                        </p>
                                    )}
                                    </div>
                                )
                            })}
                            {factorsDiffer && (
                                // The stored list can't be satisfied on this server (SMS only,
                                // without an SMS provider): the strong kinds apply instead.
                                <p className="text-xs text-foreground rounded-md border border-border bg-muted/40 p-2" data-testid="security-mfa-factors-effective">
                                    {t('security.mfa.factors_effective', {
                                        list: (effective.mfa_allowed_factors ?? []).map((k) => t(`mfa.factor_kinds.${k}`, { ns: 'auth' })).join(', '),
                                    })}
                                </p>
                            )}
                        </div>
                    </div>

                    <label className="flex items-start gap-3">
                        <Checkbox
                            checked={own.trust_idp_mfa}
                            onCheckedChange={(checked) => save({ trust_idp_mfa: checked })}
                            disabled={disabled}
                            className="mt-0.5"
                            data-testid="security-trust-idp-mfa"
                        />
                        <span>
                            <span className="block text-sm font-medium text-foreground">{t('security.mfa.trust_idp_label')}</span>
                            <span className="block text-xs text-muted-foreground mt-1">{t('security.mfa.trust_idp_hint')}</span>
                        </span>
                    </label>
                </section>

                <section className="bg-card rounded-lg border border-border p-6" data-testid="security-effective-policy">
                    <h2 className="text-base font-semibold text-foreground">{t('security.effective.title')}</h2>
                    <p className="text-muted-foreground text-sm mt-1 mb-4">{t('security.effective.description')}</p>
                    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                        <div>
                            <dt className="text-muted-foreground">{t('security.max_duration.label')}</dt>
                            <dd className="font-medium text-foreground" data-testid="security-effective-max-duration">
                                {formatDuration(effective.session_max_duration, t)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">{t('security.idle_timeout.label')}</dt>
                            <dd className="font-medium text-foreground" data-testid="security-effective-idle-timeout">
                                {effective.session_idle_timeout
                                    ? formatDuration(effective.session_idle_timeout, t)
                                    : t('security.idle_timeout.none')}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">{t('security.max_sessions.label')}</dt>
                            <dd className="font-medium text-foreground" data-testid="security-effective-max-sessions">
                                {effective.max_sessions_per_user ?? policy.limits.max_sessions_per_user}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">{t('security.mfa.effective_required')}</dt>
                            <dd className="font-medium text-foreground" data-testid="security-effective-mfa">
                                {effective.mfa_required ? t('security.mfa.required_yes') : t('security.mfa.required_no')}
                            </dd>
                        </div>
                    </dl>

                    {platform.mfa_required && platform.enforced && (
                        <p className="mt-4 text-sm text-muted-foreground" data-testid="security-platform-mfa">
                            {t('security.mfa.platform_required')}
                        </p>
                    )}

                    {(platform.session_max_duration != null || platform.max_sessions_per_user != null) && (
                        <div
                            className="mt-4 flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground"
                            data-testid="security-platform-floor"
                        >
                            <AppIcon
                                path={floorStricter ? mdiAlertOutline : mdiInformationOutline}
                                size="sm"
                                className="mt-0.5 flex-shrink-0"
                            />
                            <div>
                                <p>
                                    {t('security.platform.summary', {
                                        duration: formatDuration(platform.session_max_duration, t),
                                        sessions: platform.max_sessions_per_user ?? '—',
                                    })}
                                </p>
                                <p className="mt-1">
                                    {platform.enforced ? t('security.platform.enforced') : t('security.platform.not_enforced')}
                                </p>
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </div>
    )
}
